import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/** Adds the same note to every selected panel. */
export function BulkNoteDialog({ projectId, panelUids, open, onOpenChange, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/panels/comments/bulk`, {
        panel_uids: panelUids,
        text,
      });
      toast.success(`Note added to ${data.created} panel(s)`);
      setText("");
      onOpenChange(false);
      onDone?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="bulk-note-dialog"
        className="max-w-lg border-slate-200 bg-white text-slate-900"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg">
            Add note to {panelUids?.length || 0} panel(s)
          </DialogTitle>
        </DialogHeader>
        <Textarea
          data-testid="bulk-note-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="The same note is saved on every selected panel…"
          className="min-h-28 border-slate-200 bg-[#F8FAFC] text-sm"
        />
        <Button
          data-testid="bulk-note-submit"
          onClick={submit}
          disabled={busy || !text.trim()}
          className="h-10 rounded-full bg-cyan-600 text-[11px] uppercase tracking-wider text-white hover:bg-cyan-500 disabled:opacity-60"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} className="mr-2" />}
          Post note
        </Button>
      </DialogContent>
    </Dialog>
  );
}
